/**
 * @file Renders the contact-us form, sending it to the guest or the
 * investor notification operation depending on who submits it.
 */
import type { FormInstance } from "antd";

import { EntityForm } from "@components/Form";
import type { AppContext } from "@components/AppContext/types";
import {
  sendContactUsGuestNotificationOperation as guestOperation,
  sendContactUsInvestorNotificationOperation as investorOperation,
} from "@api/services/notifications";

import getGuestInputs from "./helpers/getGuestInputs";
import getInvestorInputs from "./helpers/getInvestorInputs";

const LABEL_ENTITY = "Request";
const LABEL_SUBMIT = "Send";
const LABEL_SUCCESS = "Notification has been sent";

export type Props = {
  isGuest?: boolean;
  projectId?: string;
  organizationId?: string;
  form: FormInstance;
  request: AppContext["request"];
  onSuccess: () => unknown;
};

/**
 * Renders the contact-us request form, picking its inputs, schema and
 * submit operation based on whether the requester is a guest or investor.
 */
const ContactForm = ({
  isGuest = false,
  projectId = undefined,
  organizationId = undefined,
  form,
  request,
  onSuccess,
}: Props): JSX.Element => {
  const inputs = isGuest
    ? getGuestInputs(organizationId, projectId)
    : getInvestorInputs();

  const schema = isGuest
    ? guestOperation.getSchema(inputs)
    : investorOperation.getSchema(inputs);

  const operation = isGuest
    ? guestOperation
    : investorOperation;

  return (
    <EntityForm
      form={form}
      schema={schema}
      entity={LABEL_ENTITY}
      request={request}
      operation={operation}
      onSuccess={onSuccess}
      submitLabel={LABEL_SUBMIT}
      successMessage={LABEL_SUCCESS}
    />
  );
};

export default ContactForm;
